import { Card, Skeleton, Spin } from 'antd';
import { get } from 'lodash';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { useAuthedQuery, useAuthedQueries, useSession } from '../lib/hooks';
import RULES from '../access_rights';
import UIButtonLink from './ui/ButtonLink';
import ErrorMessageForbidden from '../pages/403';
const ruleFromHref = ({
  pathname,
  query
}) => {
  const {
    id,
    slug
  } = query ?? {};
  return {
    action: 'page',
    object: pathname.split('/').filter(Boolean).join('/'),
    args: [{
      id,
      slug
    }]
  };
};
const useQuery = rule => {
  const {
    user
  } = useSession();
  const {
    args = [],
    action,
    object
  } = rule;
  const [accessFn, accessFnKey] = RULES.get(action, object);
  const res = useAuthedQuery({
    queryKey: ['access_control', accessFnKey, ...args],
    queryFn: () => accessFn(user, ...args)
  });
  const accessible = res.isPending ? null : res.isSuccess && !!res.data;
  return [accessible, res];
};
const useQueries = rules => {
  const {
    user
  } = useSession();
  const queries = useMemo(() => ({
    queries: rules.map(rule => {
      const {
        args = [],
        action,
        object
      } = rule;
      const [accessFn, accessFnKey] = RULES.get(action, object);
      return {
        queryKey: ['access_control', accessFnKey, ...args],
        queryFn: () => accessFn(user, ...args),
        notifyOnChangeProps: ['isPending', 'isSuccess', 'data']
      };
    })
  }), [user, rules]);
  const results = useAuthedQueries(queries);
  const isPending = results.some(res => res.isPending);
  const accessible = isPending ? [] : results.map(res => res.isSuccess && !!res.data);
  return {
    results,
    isPending,
    accessible
  };
};
const CheckAccess = ({
  rule,
  children,
  ifCant = null,
  onChecked,
  loadType
}) => {
  const [accessible, {
    isPending
  }] = useQuery(rule);
  useEffect(() => {
    !isPending && onChecked && onChecked(accessible);
  }, [onChecked, accessible, isPending]);
  return isPending ? loadType || <Spin /> : accessible ? children : ifCant;
};
const LinkLabel = ({
  href,
  label
}) => {
  const route = useRouter();
  const cls = ['pathname', 'query.slug', 'query.id'].every(k => get(route, k) === get(href, k)) ? 'current' : '';
  return <NextLink href={href} className={cls} children={label} data-sentry-element="NextLink" data-sentry-component="LinkLabel" data-sentry-source-file="AccessControl.jsx" />;
};
const Link = ({
  href,
  rule,
  label,
  loadType,
  ...rest
}) => <CheckAccess {...rest} rule={rule ?? ruleFromHref(href)} loadType={loadType || <Skeleton.Button active={true} size="small" shape="default" />} children={<LinkLabel href={href} label={label} />} data-sentry-element="CheckAccess" data-sentry-component="Link" data-sentry-source-file="AccessControl.jsx" />;
const ButtonLink = ({
  href,
  rule,
  label,
  ifCant,
  onChecked,
  loadType,
  ...rest
}) => <CheckAccess ifCant={ifCant} onChecked={onChecked} loadType={loadType || <Skeleton.Button active={true} block={true} />} rule={rule ?? ruleFromHref(href)} children={<UIButtonLink url={href} children={label} {...rest} />} data-sentry-element="CheckAccess" data-sentry-component="ButtonLink" data-sentry-source-file="AccessControl.jsx" />;
const Page = ({
  children
}) => <CheckAccess rule={ruleFromHref(useRouter())} loadType={<Card loading={true} style={{
  width: '100%'
}} />} ifCant={<ErrorMessageForbidden />} children={children} data-sentry-element="CheckAccess" data-sentry-component="Page" data-sentry-source-file="AccessControl.jsx" />;
export default {
  CheckAccess,
  Link,
  ButtonLink,
  Page,
  ruleFromHref,
  useQuery,
  useQueries
};